import { useRef, useState } from "react";
import { Eraser, Highlighter, PenLine, Trash2, Undo2, X } from "lucide-react";
import { uid } from "@/lib/api";
import { Button } from "@/components/ui/button";

export const INK = [["#1f2428", "Charcoal"], ["#b1492f", "Accent"], ["#3f5f4f", "Sage"], ["#f5f1e8", "Chalk"]];
const WIDTHS = [["s", 2.5], ["m", 5], ["l", 10]];

function pathOf(points) {
  if (points.length === 1) { const [x, y] = points[0]; return `M${x} ${y} L${x + 0.1} ${y}`; }
  return points.map(([x, y], i) => `${i ? "L" : "M"}${x.toFixed(1)} ${y.toFixed(1)}`).join(" ");
}

export function strokeWidth(s) {
  const p = s.points.reduce((a, pt) => a + (pt[2] || 0.5), 0) / s.points.length;
  return (s.tool === "marker" ? s.width * 3 : s.width) * (0.5 + p);
}

export function SketchPaths({ strokes }) {
  return strokes.map((s) => <path key={s.id} d={pathOf(s.points)} fill="none" stroke={s.color} strokeWidth={strokeWidth(s)} strokeLinecap="round" strokeLinejoin="round" opacity={s.tool === "marker" ? 0.35 : 1} data-testid={`sketch-stroke-${s.id}`} />);
}

export function SketchLayer({ strokes, roomId, active, tool, color, width, zoom, onChange }) {
  const [live, setLive] = useState(null);
  const penSeen = useRef(false);
  const svgRef = useRef(null);
  const pos = (e) => { const r = svgRef.current.getBoundingClientRect(); return [(e.clientX - r.left) / zoom, (e.clientY - r.top) / zoom, e.pressure || 0.5]; };
  const reject = (e) => { if (e.pointerType === "pen") penSeen.current = true; return e.pointerType === "touch" && penSeen.current; };
  const erase = (e) => {
    const [x, y] = pos(e); const r = 14 / zoom;
    const hit = strokes.find((s) => s.points.some(([px, py]) => Math.hypot(px - x, py - y) < r + strokeWidth(s) / 2));
    if (hit) onChange(strokes.filter((s) => s.id !== hit.id));
  };
  const down = (e) => {
    if (reject(e) || e.button > 0) return;
    e.stopPropagation(); try { svgRef.current.setPointerCapture(e.pointerId); } catch {}
    if (tool === "eraser") return erase(e);
    setLive({ id: uid(), room_id: roomId, tool, color, width, points: [pos(e)] });
  };
  const move = (e) => {
    if (reject(e)) return;
    if (tool === "eraser") { if (e.buttons) erase(e); return; }
    if (live) setLive({ ...live, points: [...live.points, pos(e)] });
  };
  const up = () => { if (live) { onChange([...strokes, live]); setLive(null); } };
  return (
    <svg ref={svgRef} className={`absolute inset-0 ${active ? "z-[997] touch-none" : "pointer-events-none z-[996]"}`} width="2400" height="1600" viewBox="0 0 2400 1600" style={active ? { cursor: tool === "eraser" ? "cell" : "crosshair" } : undefined}
      onPointerDown={active ? down : undefined} onPointerMove={active ? move : undefined} onPointerUp={active ? up : undefined} onPointerCancel={active ? up : undefined} data-testid="sketch-layer" data-active={active ? "1" : "0"}>
      <SketchPaths strokes={strokes} />
      {live && <SketchPaths strokes={[live]} />}
    </svg>
  );
}

export function SketchTools({ tool, setTool, color, setColor, width, setWidth, onUndo, onClear, onDone, canUndo }) {
  return (
    <div className="absolute left-3 top-3 z-20 flex flex-wrap items-center gap-1 rounded-full border bg-card/95 p-1 shadow backdrop-blur" data-testid="sketch-tools">
      {[["pen", PenLine, "Pen"], ["marker", Highlighter, "Marker"], ["eraser", Eraser, "Eraser"]].map(([k, Icon, label]) => (
        <Button key={k} variant={tool === k ? "secondary" : "ghost"} size="icon" className="h-8 w-8 rounded-full" aria-label={label} aria-pressed={tool === k} onClick={() => setTool(k)} data-testid={`sketch-tool-${k}`}><Icon className="h-4 w-4" /></Button>
      ))}
      <span className="mx-1 h-5 w-px bg-border" />
      {INK.map(([hex, name]) => <button key={hex} type="button" aria-label={name} aria-pressed={color === hex} onClick={() => setColor(hex)} data-testid={`sketch-color-${name.toLowerCase()}`} className={`h-6 w-6 rounded-full border-2 ${color === hex ? "border-accent" : "border-transparent"}`} style={{ background: hex, boxShadow: "inset 0 0 0 1px rgba(0,0,0,.15)" }} />)}
      <span className="mx-1 h-5 w-px bg-border" />
      {WIDTHS.map(([k, w]) => <button key={k} type="button" aria-label={`Width ${k}`} aria-pressed={width === w} onClick={() => setWidth(w)} data-testid={`sketch-width-${k}`} className={`flex h-7 w-7 items-center justify-center rounded-full ${width === w ? "bg-secondary" : ""}`}><span className="rounded-full bg-foreground" style={{ width: Math.min(w * 1.6, 14), height: Math.min(w * 1.6, 14) }} /></button>)}
      <span className="mx-1 h-5 w-px bg-border" />
      <Button variant="ghost" size="icon" className="h-8 w-8 rounded-full" aria-label="Undo stroke" disabled={!canUndo} onClick={onUndo} data-testid="sketch-undo"><Undo2 className="h-4 w-4" /></Button>
      <Button variant="ghost" size="icon" className="h-8 w-8 rounded-full" aria-label="Clear sketches in this room" disabled={!canUndo} onClick={onClear} data-testid="sketch-clear"><Trash2 className="h-4 w-4" /></Button>
      <Button variant="outline" size="sm" className="h-8 rounded-full" onClick={onDone} data-testid="sketch-done"><X className="mr-1 h-3.5 w-3.5" />Done</Button>
    </div>
  );
}
